import { useEffect, useState, useCallback } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { EXPENSE_CATEGORIES, LIVESTOCK } from "../lib/constants";
import { StatCard } from "../components/StatCard";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Trash2, Pencil, TrendingDown, Clock, Wallet, Repeat, RefreshCw, Power } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { exportPdf } from "../lib/pdf";

const blank = () => ({
  type: "expense",
  category: "",
  description: "",
  amount: 0,
  status: "paid",
  party: "",
  livestock_type: "general",
  date: new Date().toISOString().slice(0, 10),
});

const recBlank = () => ({ category: "", description: "", amount: 0, party: "", livestock_type: "general", day_of_month: 1, active: true, flow: "expense" });

export default function Expenses() {
  const { fmt, settings } = useFarm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  const load = useCallback(() => api.getTransactions().then((all) => setItems(all.filter((t) => t.type === "expense"))), []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e.id);
    setForm({
      type: "expense", category: e.category, description: e.description || "",
      amount: e.amount, status: e.status, party: e.party || "",
      livestock_type: e.livestock_type || "general", date: e.date,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.category || !form.amount) return toast.error("Category and amount are required");
    if (editing) {
      await api.updateTransaction(editing, form);
      toast.success("Expense updated");
    } else {
      await api.createTransaction(form);
      toast.success("Expense added");
    }
    setOpen(false);
    setEditing(null);
    setForm(blank());
    load();
  };
  const remove = async (id) => { await api.deleteTransaction(id); load(); toast.success("Deleted"); };

  const [recurring, setRecurring] = useState([]);
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState(recBlank());
  const loadRec = useCallback(() => api.getRecurring("expense").then(setRecurring), []);
  useEffect(() => { loadRec(); }, [loadRec]);
  const addRec = async () => {
    if (!recForm.category || !recForm.amount) return toast.error("Category and amount required");
    await api.createRecurring(recForm);
    toast.success("Recurring expense added");
    setRecOpen(false); setRecForm(recBlank()); loadRec();
  };
  const toggleRec = async (r) => { await api.updateRecurring(r.id, { ...r, active: !r.active }); loadRec(); };
  const delRec = async (id) => { await api.deleteRecurring(id); loadRec(); toast.success("Removed"); };
  const runDue = async () => {
    const res = await api.runRecurring();
    toast.success(res.posted ? `Posted ${res.posted} recurring expense(s)` : "Nothing due this month");
    load(); loadRec();
  };

  const totalPaid = items.filter((e) => e.status === "paid").reduce((s, e) => s + e.amount, 0);
  const totalUpcoming = items.filter((e) => e.status === "upcoming").reduce((s, e) => s + e.amount, 0);
  const downloadPdf = () => exportPdf({
    settings, title: "Expenses & Bills", filename: "expenses-bills.pdf",
    sections: [{
      columns: ["Date", "Category", "Description", "Supplier", "Status", "Amount"],
      rows: items.map((e) => [e.date, e.category, e.description, e.party, e.status, { money: e.amount }]),
      foot: ["", "", "", "Paid / Upcoming", "", `${settings.currency_code} ${totalPaid.toFixed(2)} / ${totalUpcoming.toFixed(2)}`],
    }],
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Expenses</h1>
          <p className="text-sm text-muted-foreground mt-1">Record, update, and track paid and upcoming farm expenses.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="h-10 rounded-full gap-1.5" onClick={downloadPdf} disabled={!items.length} data-testid="expenses-export-pdf-button"><FileDown className="h-4 w-4" /> Export PDF</Button>
          <Button className="h-10 rounded-full gap-1.5" onClick={openAdd} data-testid="add-expense-button">
            <Plus className="h-4 w-4" /> Add Expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="expense-total-paid" label="Total Paid" value={fmt(totalPaid)} sub="Settled expenses" icon={TrendingDown} tone="terracotta" />
        <StatCard testid="expense-total-upcoming" label="Upcoming" value={fmt(totalUpcoming)} sub="Scheduled outgoing" icon={Clock} tone="accent" delay={60} />
        <StatCard testid="expense-count" label="Records" value={items.length} sub="Expense entries" icon={Wallet} tone="primary" delay={120} />
      </div>

      <Card className="overflow-hidden">
        {items.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="expenses-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Livestock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="font-mono-nums text-sm">{e.date}</TableCell>
                    <TableCell className="font-medium">{e.category}{e.description && <span className="block text-xs text-muted-foreground">{e.description}</span>}</TableCell>
                    <TableCell className="text-sm">{e.party || "—"}</TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{e.livestock_type}</Badge></TableCell>
                    <TableCell><Badge variant={e.status === "paid" ? "default" : "secondary"} className="capitalize">{e.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono-nums font-semibold">{fmt(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="py-20 text-center">
            <Wallet className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">No expenses recorded yet.</p>
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="recurring-section">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-accent" />
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Recurring Expenses</h2>
              <p className="text-xs text-muted-foreground">Auto-posted on the 1st of each month.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={runDue} data-testid="run-recurring-button"><RefreshCw className="h-3.5 w-3.5" /> Run Due Now</Button>
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => { setRecForm(recBlank()); setRecOpen(true); }} data-testid="add-recurring-button"><Plus className="h-3.5 w-3.5" /> Add Recurring</Button>
          </div>
        </div>
        {recurring.length ? (
          <div className="space-y-2">
            {recurring.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3.5" data-testid={`recurring-row-${r.id}`}>
                <div className="flex-1 min-w-[160px]">
                  <p className="font-semibold">{r.category} <span className="font-mono-nums text-muted-foreground">· day {r.day_of_month}</span></p>
                  <p className="text-xs text-muted-foreground">{r.party || "—"}{r.description ? ` · ${r.description}` : ""}</p>
                </div>
                <span className="font-mono-nums font-bold">{fmt(r.amount)}</span>
                <div className="flex items-center gap-1.5">
                  <Power className={`h-3.5 w-3.5 ${r.active ? "text-[hsl(var(--emerald))]" : "text-muted-foreground"}`} />
                  <Switch checked={r.active} onCheckedChange={() => toggleRec(r)} data-testid={`toggle-recurring-${r.id}`} />
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delRec(r.id)} data-testid={`delete-recurring-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">No recurring expenses set up yet.</p>
        )}
      </Card>

      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Add Recurring Expense</DialogTitle>
            <DialogDescription>This will auto-post as a paid expense every month.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Category</Label>
              <Select value={recForm.category} onValueChange={(v) => setRecForm({ ...recForm, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="recurring-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount ({settings.currency_symbol})</Label>
              <Input type="number" className="mt-1.5" value={recForm.amount} onChange={(e) => setRecForm({ ...recForm, amount: parseFloat(e.target.value) || 0 })} data-testid="recurring-amount-input" />
            </div>
            <div>
              <Label>Day of Month</Label>
              <Input type="number" min="1" max="28" className="mt-1.5" value={recForm.day_of_month} onChange={(e) => setRecForm({ ...recForm, day_of_month: parseInt(e.target.value) || 1 })} data-testid="recurring-day-input" />
            </div>
            <div className="col-span-2">
              <Label>Supplier</Label>
              <Input className="mt-1.5" value={recForm.party} onChange={(e) => setRecForm({ ...recForm, party: e.target.value })} data-testid="recurring-party-input" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input className="mt-1.5" value={recForm.description} onChange={(e) => setRecForm({ ...recForm, description: e.target.value })} data-testid="recurring-description-input" />
            </div>
          </div>
          <DialogFooter><Button className="rounded-full" onClick={addRec} data-testid="save-recurring-button">Add Recurring</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Update Expense" : "Add Expense"}</DialogTitle>
            <DialogDescription>{editing ? "Modify the details of this expense." : "Record a new farm expense."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="expense-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="expense-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="upcoming">Upcoming</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount ({settings.currency_symbol})</Label>
              <Input type="number" className="mt-1.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} data-testid="expense-amount-input" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" />
            </div>
            <div>
              <Label>Supplier</Label>
              <Input className="mt-1.5" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} data-testid="expense-party-input" />
            </div>
            <div>
              <Label>Livestock</Label>
              <Select value={form.livestock_type} onValueChange={(v) => setForm({ ...form, livestock_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="expense-livestock-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  {LIVESTOCK.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full" onClick={submit} data-testid="save-expense-button">{editing ? "Save Changes" : "Add Expense"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
